import { useMemo } from 'react';
import { NotificationCountType } from '../constants/matrixEvents';
import logger from '../utils/logger';
import { getEffectiveReadMarkerEventId } from '../utils/unreadCountUtils';

/**
 * Manually calculate unread message count for a room.
 *
 * This hook implements the Netsfere approach of client-side unread counting
 * instead of relying solely on the Matrix SDK's notification count.
 *
 * Why manual counting?
 * - SDK's notification count may not update immediately after read receipts
 * - Private read receipts (m.read.private) don't always trigger RoomEvent.UnreadNotifications
 * - Gives us control over what counts as "unread" (e.g., excluding own messages)
 *
 * Data Source (Hybrid Approach):
 * - Primary: Uses messageGroups if provided (UI-rendered data)
 * - Fallback: Uses room.getLiveTimeline().getEvents() (server timeline)
 *
 * Algorithm (based on Netsfere's AppViewModelContainers.js:258-274):
 * 1. Get the timeline of events (from messageGroups or room timeline)
 * 2. Find the last read event ID
 * 3. Count messages after that point that:
 *    - Are NOT from the current user
 *    - Are actual messages (m.room.message type)
 *    - Haven't been redacted
 *
 * @param {Room} room - Matrix room object
 * @param {MatrixClient} matrixClient - Matrix client instance
 * @param {Array} messageGroups - Optional message groups from UI (for hybrid approach)
 * @returns {{
 *   count: number,
 *   isReliable: boolean,
 *   source: 'manual' | 'sdk-fallback' | 'error',
 *   reason?: string
 * }} Object containing unread count and metadata about its reliability
 */
export function useManualUnreadCount(room, matrixClient, messageGroups = null) {
  const manualCount = useMemo(() => {
    if (!room || !matrixClient) {
      return {
        count: 0,
        isReliable: true,
        source: 'manual',
        reason: 'No room or client',
      };
    }

    try {
      const userId = matrixClient.getUserId();
      const lastReadEventId = getEffectiveReadMarkerEventId(room, userId);

      // Hybrid data source: Try messageGroups first (UI data), fallback to timeline (server data)
      let timeline;
      let dataSource;

      if (messageGroups && messageGroups.length > 0) {
        // Flatten messageGroups to create timeline-like events array
        timeline = messageGroups.flatMap(group =>
          (group.messages || [])
            .filter(msg => {
              // Filter out invalid messages
              if (!msg) return false;
              // For Matrix event objects, ensure they have getId method
              if (typeof msg.getId === 'function') return true;
              // For plain objects, ensure they have eventId property
              if (msg.eventId) return true;
              return false;
            })
            .map(msg => {
              // Handle both Matrix event objects (with methods) and plain objects (with properties)
              if (typeof msg.getId === 'function') {
                // Already a Matrix event object - use as-is
                return msg;
              } else {
                // Plain object with properties - wrap it
                return {
                  getId: () => msg.eventId,
                  getSender: () => group.senderId,
                  getType: () => msg.type || 'm.room.message',
                  isRedacted: () => msg.redacted || false,
                };
              }
            }),
        );
        dataSource = 'messageGroups';
      } else {
        // Fallback to room timeline
        timeline = room.getLiveTimeline().getEvents();
        dataSource = 'timeline';
      }

      logger.debug(
        `[useManualUnreadCount] Room: ${room.roomId}, Source: ${dataSource}, ` +
        `Events: ${timeline.length}, LastReadEventId: ${lastReadEventId}`,
      );

      // If no last read marker, fall back to SDK count (common for new rooms)
      if (!lastReadEventId) {
        const sdkCount = room.getUnreadNotificationCount(NotificationCountType.Total);
        logger.info(
          `[useManualUnreadCount] No read marker for room ${room.roomId}. ` +
          `Timeline has ${timeline.length} events. Falling back to SDK count: ${sdkCount}.`,
        );
        return {
          count: sdkCount,
          isReliable: false,
          source: 'sdk-fallback',
          reason: 'No read marker found',
        };
      }

      // Find index of last read event
      const lastReadIndex = timeline.findIndex(e => e.getId() === lastReadEventId);

      if (lastReadIndex === -1) {
        // This is expected with pagination - read marker may be outside current timeline window
        const sdkCount = room.getUnreadNotificationCount(NotificationCountType.Total);
        logger.info(
          `[useManualUnreadCount] Read marker ${lastReadEventId} not found in timeline ` +
          `of ${timeline.length} events for room ${room.roomId}. ` +
          `This is normal with pagination. Falling back to SDK count: ${sdkCount}.`,
        );
        return {
          count: sdkCount,
          isReliable: false,
          source: 'sdk-fallback',
          reason: 'Read marker not in timeline (pagination)',
        };
      }

      // Count messages after lastReadIndex that aren't from current user
      let count = 0;
      for (let i = lastReadIndex + 1; i < timeline.length; i++) {
        const event = timeline[i];

        // Skip own messages (like Netsfere does)
        if (event.getSender() === userId) {
          continue;
        }

        // Only count actual messages and reactions
        if (event.getType() !== 'm.room.message' && event.getType() !== 'm.reaction') {
          continue;
        }

        // Skip redacted messages
        if (event.isRedacted()) {
          continue;
        }

        count++;
      }

      logger.debug(`[useManualUnreadCount] Room: ${room.roomId}, Manual count: ${count}, Last read index: ${lastReadIndex}`);

      return {
        count,
        isReliable: true,
        source: 'manual',
      };
    } catch (error) {
      logger.error('[useManualUnreadCount] Error calculating manual unread count:', error);
      const sdkCount = room?.getUnreadNotificationCount(NotificationCountType.Total) || 0;
      return {
        count: sdkCount,
        isReliable: false,
        source: 'error',
        reason: error.message,
      };
    }
  }, [room, matrixClient, messageGroups]);

  return manualCount;
}
